import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, PencilSimple, Trash, Printer, WhatsappLogo, EnvelopeSimple, Bell, MagnifyingGlass, X, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import QRCodeButton from "@/components/QRCodeButton";
import PageExportBar from "@/components/PageExportBar";
import { openPrintStatement } from "@/lib/printStatement";

export default function SalesInvoices() {
  const { activeCompanyId, companies, activeCompany, can, activeStoreId, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, custRes] = await Promise.all([
        api.get("/sales-invoices"),
        api.get("/customers"),
      ]);
      setRows(invRes.data);
      const map = {};
      custRes.data.forEach((c) => { map[c.id] = c; });
      setCustomers(map);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId]);

  const del = async (id) => {
    if (!confirm("Delete this invoice?")) return;
    try { await api.delete(`/sales-invoices/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const shareWhatsApp = (inv) => {
    const c = customers[inv.customer_id];
    const company = companies.find((x) => x.id === inv.company_id) || companies[0];
    const shareUrl = `${window.location.origin}/p/invoice/${inv.id}?t=${inv.share_token}`;
    const text = `Hello,\n\nPlease find your invoice from ${company?.name || "us"}:\n\n*Invoice ${inv.number}*\nDate: ${inv.date}\nAmount: ${inr(inv.grand_total)}\n${inv.balance_due > 0 ? `Balance Due: ${inr(inv.balance_due)}\n` : ""}\nView / Download: ${shareUrl}\n\nThank you for your business!`;
    const digits = String(c?.phone || "").replace(/[^0-9]/g, "");
    const url = digits ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (start && r.date < start) return false;
      if (end && r.date > end) return false;
      if (status !== "all" && r.status !== status) return false;
      if (qn) {
        const cust = (r.customer_name || customers[r.customer_id]?.name || "").toLowerCase();
        const num = (r.number || "").toLowerCase();
        if (!cust.includes(qn) && !num.includes(qn)) return false;
      }
      return true;
    });
  }, [rows, q, start, end, status, customers]);

  const totals = useMemo(() => {
    const grand = filtered.reduce((s, r) => s + Number(r.grand_total || 0), 0);
    const bal = filtered.reduce((s, r) => s + Number(r.balance_due || 0), 0);
    return { count: filtered.length, grand, bal };
  }, [filtered]);

  const resetFilters = () => { setQ(""); setStart(""); setEnd(""); setStatus("all"); };
  const hasActiveFilters = q || start || end || status !== "all";

  const printStatement = () => {
    if (!filtered.length) { toast.error("No invoices in the current filter."); return; }
    // Group by status: paid / partial / unpaid / cancelled → each becomes a section
    const groups = { paid: [], partial: [], unpaid: [], cancelled: [] };
    filtered.forEach((r) => (groups[r.status] || (groups[r.status] = [])).push(r));
    const label = { paid: "Paid Invoices", partial: "Partially Paid", unpaid: "Unpaid / Open", cancelled: "Cancelled" };
    const cols = [
      { key: "number", label: "Invoice #", mono: true },
      { key: "date", label: "Date", mono: true },
      { key: "customer", label: "Customer" },
      { key: "amount", label: "Amount", align: "right", mono: true },
      { key: "balance", label: "Balance Due", align: "right", mono: true },
    ];
    const sections = Object.keys(groups).filter((k) => groups[k].length).map((k) => {
      const list = groups[k];
      const sub = list.reduce((s, r) => s + Number(r.grand_total || 0), 0);
      const bal = list.reduce((s, r) => s + Number(r.balance_due || 0), 0);
      return {
        heading: label[k] || k.toUpperCase(),
        subheading: `${list.length} invoice(s) · Total <b>${inr(sub)}</b>${bal ? ` · Balance Due <b>${inr(bal)}</b>` : ""}`,
        columns: cols,
        rows: list.map((r) => ({
          number: r.number,
          date: r.date,
          customer: r.customer_name || customers[r.customer_id]?.name || "—",
          amount: inr(r.grand_total),
          balance: inr(r.balance_due),
        })),
      };
    });
    const range = start || end ? `${start || "…"} → ${end || "…"}` : "All dates";
    const res = openPrintStatement({
      title: `Sales Invoices Statement (${range})`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Invoice Count", value: String(totals.count) },
        { label: "Grand Total", value: inr(totals.grand), tone: "pos" },
        { label: "Balance Due", value: inr(totals.bal), tone: totals.bal > 0 ? "neg" : "" },
      ],
      note: hasActiveFilters ? `Filters applied — ${totals.count} invoice(s) shown.` : "Full invoice list.",
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${totals.count} invoice(s)…`);
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Sales</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Sales Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {totals.count} of {rows.length} invoice(s) · Total <b>{inr(totals.grand)}</b>{totals.bal ? <> · Balance Due <b className="text-destructive">{inr(totals.bal)}</b></> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="h-9" onClick={printStatement} data-testid="sales-print-statement">
            <Printer size={16} className="mr-1.5" /> Print Statement
          </Button>
          <PageExportBar
            title="Sales Invoices"
            filename={`sales-invoices-${new Date().toISOString().slice(0,10)}`}
            columns={[
              { key: "number", label: "Invoice #" },
              { key: "date", label: "Date" },
              { key: "customer", label: "Customer", get: (r) => r.customer_name || customers[r.customer_id]?.name || "—" },
              { key: "grand_total", label: "Amount" },
              { key: "balance_due", label: "Balance Due" },
              { key: "status", label: "Status" },
            ]}
            rows={filtered}
            testidPrefix="sales"
          />
          <Link to="/sales/new">
            <Button className="h-9" data-testid="new-sale-btn">
              <Plus size={15} weight="bold" className="mr-1.5" /> New Invoice
            </Button>
          </Link>
        </div>
      </div>

      <div className="card-flat p-3 flex flex-wrap items-end gap-3 no-print">
        <div className="space-y-1.5">
          <Label className="eyebrow">From Date</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)}
            className="h-9 font-mono w-40" data-testid="sales-filter-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To Date</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)}
            className="h-9 font-mono w-40" data-testid="sales-filter-end" />
        </div>
        <div className="space-y-1.5 flex-1 min-w-[220px]">
          <Label className="eyebrow">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Invoice # or customer name"
              className="pl-9 h-9" data-testid="sales-filter-q" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="sales-filter-status">
            <option value="all">All</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" className="h-9" onClick={resetFilters} data-testid="sales-filter-reset">
            <X size={14} className="mr-1.5" /> Clear
          </Button>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Status</th>
              <th className="num">Grand Total</th>
              <th className="num">Balance Due</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">
                {rows.length === 0 ? "No invoices yet." : "No invoices match the current filter."}
              </td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`inv-row-${r.id}`}>
                <td className="font-mono text-[12.5px]">
                  <Link to={`/sales/${r.id}`} className="text-primary hover:underline">{r.number}</Link>
                </td>
                <td>{fmtDate(r.date)}</td>
                <td>{r.customer_name}</td>
                <td><span className={`pill ${r.status === "paid" ? "pill-success" : r.status === "partial" ? "pill-warning" : r.status === "cancelled" ? "pill-danger" : "pill-info"}`}>{r.status}</span></td>
                <td className="num font-semibold">{inr(r.grand_total)}</td>
                <td className="num">{inr(r.balance_due)}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  <QRCodeButton
                    url={`${window.location.origin}/p/invoice/${r.id}?t=${r.share_token}`}
                    label={`QR · ${r.number}`}
                    testid={`inv-qr-${r.id}`}
                  />
                  <Button variant="ghost" size="icon" className="h-8 w-8"
                    style={{ color: "#128C7E" }}
                    title="Send via WhatsApp"
                    onClick={() => shareWhatsApp(r)}
                    data-testid={`inv-whatsapp-${r.id}`}>
                    <WhatsappLogo size={15} weight="fill" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8"
                    title="Email invoice"
                    onClick={async () => {
                      try {
                        await api.post("/email/invoice", { invoice_id: r.id });
                        toast.success(`Invoice ${r.number} emailed`);
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    data-testid={`inv-email-${r.id}`}>
                    <EnvelopeSimple size={14} />
                  </Button>
                  {Number(r.balance_due || 0) > 0 && (
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      title="Email payment reminder"
                      onClick={async () => {
                        try {
                          await api.post("/email/payment-reminder", { invoice_id: r.id });
                          toast.success("Payment reminder emailed");
                        } catch (err) { toast.error(formatApiError(err)); }
                      }}
                      data-testid={`inv-remind-${r.id}`}>
                      <Bell size={14} />
                    </Button>
                  )}
                  <a
                    href={`/p/invoice/${r.id}?t=${encodeURIComponent(r.share_token || "")}&autoprint=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Print with QR Pay" data-testid={`inv-print-${r.id}`}><Printer size={14} /></Button>
                  </a>
                  <Link to={`/sales/${r.id}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      disabled={!!r.auto_generated && user?.role !== "admin"}
                      title={r.auto_generated
                        ? (user?.role === "admin"
                            ? "Admin override — edit locked auto-generated invoice"
                            : "Locked — auto-generated. Ask an admin to edit.")
                        : "Edit"}
                      data-testid={`inv-edit-${r.id}`}>
                      {r.auto_generated && user?.role !== "admin" ? <Lock size={14} /> : <PencilSimple size={14} />}
                    </Button>
                  </Link>
                  {can("sales", "delete") && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`inv-del-${r.id}`}>
                      <Trash size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
